import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import HeroCarousel from '../components/HeroCarousel';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SkeletonCard from '../components/SkeletonCard';
import { applyFilters, DEFAULT_FILTERS, hasActiveFilters } from '../components/FilterPanel';
import SEOMeta from '../components/SEOMeta';
import BrandStrip from '../components/BrandStrip';
import DealsStrip from '../components/DealsStrip';
import { API_BASE } from '../config/api';
import { getPrimaryProductImage } from '../utils/productImages';

const PREVIEW_COUNT = 6;

function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('q') || '';
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const allProducts = categories.flatMap(c => c.products);
  const filterActive = hasActiveFilters(filters);

  const stripRef = useRef(null);
  const scrollSpeedRef = useRef(0);
  const rafRef = useRef(null);

useEffect(() => {
    fetch(`${API_BASE}/categories-with-products/`)
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(data => {
        setCategories(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load products. Make sure the backend server is running.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('q') || '';
    if (urlQuery !== searchQuery) {
      setSearchQuery(urlQuery);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get('q') || '';
    const next = searchQuery.trim();

    if (next) params.set('q', next);
    else params.delete('q');

    const nextSearch = params.toString() ? `?${params.toString()}` : '';
    if (next !== current || location.search !== nextSearch) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  }, [searchQuery, location.pathname, location.search, navigate]);

  const searchResults = searchQuery
    ? applyFilters(
        categories
          .flatMap(c => c.products)
          .filter(
            p =>
              p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (p.description &&
                p.description.toLowerCase().includes(searchQuery.toLowerCase()))
          ),
        filters
      )
    : null;

  const liveResults = searchQuery.trim()
    ? categories.flatMap(c => c.products)
        .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 6)
    : [];

  // Reorder categories: laptops, monitors, desktops, phones and tablets, all in ones, servers, tvs, then the rest
  const categoryOrder = ['Laptops', 'Monitors', 'Desktops', 'Phones and Tablets', 'All in Ones', 'Servers', 'TVs'];
  const sortedCategories = [...categories].sort((a, b) => {
    const indexA = categoryOrder.indexOf(a.name);
    const indexB = categoryOrder.indexOf(b.name);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const animateScroll = useCallback(() => {
    if (stripRef.current && scrollSpeedRef.current !== 0) {
      stripRef.current.scrollLeft += scrollSpeedRef.current;
      rafRef.current = requestAnimationFrame(animateScroll);
    }
  }, []);

  const handleStripMouseMove = useCallback((e) => {
    const el = stripRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Only activate scroll when the mouse is actually over the scrollable strip
    if (e.clientX < rect.left || e.clientX > rect.right) {
      scrollSpeedRef.current = 0;
      return;
    }
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    const zone = 0.15;
    cancelAnimationFrame(rafRef.current);
    if (pct < zone) {
      scrollSpeedRef.current = -((zone - pct) / zone) * 7;
      rafRef.current = requestAnimationFrame(animateScroll);
    } else if (pct > 1 - zone) {
      scrollSpeedRef.current = ((pct - (1 - zone)) / zone) * 7;
      rafRef.current = requestAnimationFrame(animateScroll);
    } else {
      scrollSpeedRef.current = 0;
    }
  }, [animateScroll]);

  const handleStripMouseLeave = useCallback(() => {
    scrollSpeedRef.current = 0;
    cancelAnimationFrame(rafRef.current);
  }, []);

  const homeBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://herclesglobal.com/' }],
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ElectronicsStore',
    '@id': 'https://herclesglobal.com/#organization',
    name: 'Hercles Global',
    legalName: 'Hercles Global',
    alternateName: ['Hercles', 'Hercles Global'],
    url: 'https://herclesglobal.com/',
    logo: 'https://herclesglobal.com/hercles-logo.png',
    image: 'https://herclesglobal.com/hercles-social-card.png',
    description: "Hercles Global is a Nairobi-based electronics store selling laptops, phones, computer accessories, gaming products, TVs, audio devices and office electronics across Kenya.",
    telephone: '+254724474097',
    email: 'info@herclesglobal.com',
    priceRange: 'KES',
    paymentAccepted: ['M-Pesa', 'Visa', 'Mastercard'],
    currenciesAccepted: 'KES',
    areaServed: [
      { '@type': 'Country', name: 'Kenya' },
      { '@type': 'City', name: 'Nairobi' },
    ],
    knowsAbout: [
      'Laptops',
      'Smartphones',
      'Computer accessories',
      'Gaming accessories',
      'Monitors',
      'TVs',
      'Office electronics',
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Nairobi',
      addressCountry: 'KE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+254724474097',
      email: 'info@herclesglobal.com',
      contactType: 'customer service',
      areaServed: 'KE',
      availableLanguage: ['English', 'Swahili'],
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Electronics and computer products',
      itemListElement: [
        { '@type': 'OfferCatalog', name: 'Laptops' },
        { '@type': 'OfferCatalog', name: 'Phones and Tablets' },
        { '@type': 'OfferCatalog', name: 'Computer Accessories' },
        { '@type': 'OfferCatalog', name: 'Gaming Accessories' },
        { '@type': 'OfferCatalog', name: 'Monitors' },
        { '@type': 'OfferCatalog', name: 'TVs' },
      ],
    },
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://herclesglobal.com/#website',
    name: 'Hercles Global',
    url: 'https://herclesglobal.com/',
    publisher: { '@id': 'https://herclesglobal.com/#organization' },
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://herclesglobal.com/?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const navigationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Hercles Global main site links',
    itemListElement: [
      { '@type': 'SiteNavigationElement', position: 1, name: 'Products', url: 'https://herclesglobal.com/' },
      { '@type': 'SiteNavigationElement', position: 2, name: 'Laptops', url: 'https://herclesglobal.com/category/laptops' },
      { '@type': 'SiteNavigationElement', position: 3, name: 'Phones and Tablets', url: 'https://herclesglobal.com/category/phones-and-tablets' },
      { '@type': 'SiteNavigationElement', position: 4, name: 'Computer Accessories', url: 'https://herclesglobal.com/category/computer-accessories' },
      { '@type': 'SiteNavigationElement', position: 5, name: 'Gaming Accessories', url: 'https://herclesglobal.com/category/gaming-accessories' },
      { '@type': 'SiteNavigationElement', position: 6, name: 'Monitors', url: 'https://herclesglobal.com/category/monitors' },
      { '@type': 'SiteNavigationElement', position: 7, name: 'Contact', url: 'https://herclesglobal.com/contact' },
    ],
  };

  const homePageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'https://herclesglobal.com/#webpage',
    url: 'https://herclesglobal.com/',
    name: 'Hercles Global',
    description: "Shop laptops, phones, computer accessories, gaming products, TVs, audio devices and office electronics from Hercles Global in Nairobi, Kenya.",
    isPartOf: { '@id': 'https://herclesglobal.com/#website' },
    about: { '@id': 'https://herclesglobal.com/#organization' },
    mainEntity: { '@id': 'https://herclesglobal.com/#organization' },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: 'https://herclesglobal.com/hercles-social-card.png',
    },
  };

  return (
    <div className="home">
      <SEOMeta
        title="Hercles Global"
        noSuffix
        description="Kenya's trusted online tech store. Shop the latest laptops, smartphones, audio, gaming and accessories at unbeatable prices. Fast delivery across Nairobi and Kenya."
        canonical="/"
        robots={searchQuery.trim() ? 'noindex, follow' : undefined}
        jsonLd={[homeBreadcrumb, organizationJsonLd, websiteJsonLd, navigationJsonLd, homePageJsonLd]}
      />
      <Header
        showSearch
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        liveSearchResults={liveResults}
        onFilterToggle={() => setFilterOpen(o => !o)}
        filterActive={filterActive}
        filterOpen={filterOpen}
        filterMode="home"
        filterProducts={allProducts}
        filterCategories={categories}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Category tabs at top — hidden during search */}
      {!loading && !error && (
        <div
          className="cat-strip-outer"
          onMouseMove={handleStripMouseMove}
          onMouseLeave={handleStripMouseLeave}
        >
          {/* Categories dropdown — outside the scroll container so it isn't clipped */}
          <div className="cat-dropdown-wrapper">
            <button className="cat-dropdown-toggle" title="View all categories">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            <div className="cat-dropdown-menu">
              {sortedCategories.filter(c => c.products.length > 0).map(cat => (
                <Link key={cat.id} to={`/category/${cat.slug || cat.id}`} className="cat-dropdown-item">
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Scrollable category tabs */}
          <div
            className="cat-strip-wrapper cat-strip-top cat-strip-tabs-only"
            ref={stripRef}
          >
            {sortedCategories.filter(c => c.products.length > 0).map(cat => (
              <Link key={cat.id} to={`/category/${cat.slug || cat.id}`} className="cat-strip-tab cat-strip-tab-text-only">
                <span className="cat-strip-name">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Full-screen carousel */}
      {!loading && !error && !searchResults && (
        <HeroCarousel
          featuredProducts={(() => {
            const allProducts = categories.flatMap(c => c.products).filter(p => getPrimaryProductImage(p));

            // Pick one product by name keyword, preferring earlier matches
            const pick = (keywords) => allProducts.find(p =>
              keywords.some(kw => p.name.toLowerCase().includes(kw))
            );

            const slots = [
              pick(['airpods max', 'airpods']),
              pick(['playstation 5 slim', 'playstation 5', 'ps5 slim', 'ps5']),
              pick(['macbook', 'thinkpad', 'elitebook']),
              pick(['samsung qled', 'lg oled', 'sony bravia', 'samsung tv', 'lg tv']),
              pick(['gopro', 'canon eos', 'dji', 'fujifilm instax']),
            ];

            // Deduplicate and fill any nulls from remaining products
            const chosen = new Set();
            const result = [];
            for (const p of slots) {
              if (p && !chosen.has(p.id)) { chosen.add(p.id); result.push(p); }
            }
            for (const p of allProducts) {
              if (result.length >= 5) break;
              if (!chosen.has(p.id)) { chosen.add(p.id); result.push(p); }
            }
            return result.slice(0, 5);
          })()}
          categories={categories}
          fullScreen={true}
        />
      )}

      <main className="home-main">
        {loading && (
          <section className="category-section">
            <div className="hp-product-grid">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </section>
        )}
        {error && <p className="status-msg error">{error}</p>}

        {/* Products on Offer + Brand Strip — hidden during search */}
        {!loading && !error && !searchResults && (
          <>
            <DealsStrip allProducts={allProducts} />
            <BrandStrip categories={categories} />
          </>
        )}

        {/* Search results */}
        {searchResults && (
          <div className="search-results-section">
            <div className="category-bar">
              <span className="category-bar-name">Results for "{searchQuery}" ({searchResults.length})</span>
              <span className="category-count" style={{ color: 'rgba(255,255,255,0.8)' }}>&nbsp;</span>
            </div>
            {searchResults.length === 0 ? (
              <p className="status-msg">No products found.</p>
            ) : (
              <div className="hp-product-grid">
                {searchResults.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        )}

        {/* Category sections */}
        {!searchResults && sortedCategories
          .filter(c => c.products.length > 0)
          .map((category) => {
            const visibleProducts = filterActive
              ? applyFilters(category.products, filters).slice(0, PREVIEW_COUNT)
              : category.products.slice(0, PREVIEW_COUNT);
            return (
              <section key={category.id} className="category-section">
                <div className="category-bar">
                  <span className="category-bar-name">{category.name}</span>
                  <Link to={`/category/${category.slug || category.id}`} className="category-see-more">
                    See More →
                  </Link>
                </div>
                {visibleProducts.length === 0 ? (
                  <p className="status-msg" style={{padding:'12px 0'}}>No products match the current filters.</p>
                ) : (
                  <div className="hp-product-grid">
                    {visibleProducts.map(p => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                )}
              </section>
            );
          })
        }
      </main>
      <Footer />
    </div>
  );
}

export default Home;
